// @flow
import * as React from 'react';
import 'app/common/react/components/profile/showcase/ShowcaseContent/ShowcaseContent.scss';
import CONST from 'app/common/lib/Const';
import cx from 'classnames';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import PropTypes from 'prop-types';
import WideBoardCollage from 'app/common/react/components/profile/ProfileBoardCard/WideBoardCollage';
import ShopShowcaseSection, {
  ShowcaseShopProps,
} from 'app/common/react/components/profile/showcase/ShopShowcaseSection/ShopShowcaseSection';
import type {
  ShowcaseArticleType,
  ShowcaseBoardType,
  ShowcaseShopType,
} from 'app/common/react/components/profile/showcase/commonTypes';
import {
  ShowcaseArticleProps,
  ShowcaseBoardProps,
} from 'app/common/react/components/profile/showcase/commonTypes';
import { Icon, Mask } from 'gestalt';

const ADD_CIRCLE_ICON_SIZE = 56;
const ARTICLE_COVER_IMAGE_SIZE = '564x';
const BOARD_THUMB_IMAGE_SIZE = '550x';
const SHOWCASE_BOARD_REP_COLUMNS = 4;
const SHOWCASE_CONTENT_HEIGHT = 377;
const SHOWCASE_CONTENT_WIDTH = 768;

type ShowcaseContentProps = {
  currentSection: ?(ShowcaseBoardType | ShowcaseArticleType | ShowcaseShopType),
  onClick?: Function,
  onEmptyStateDismissal?: Function,
  viewingOwnProfile: boolean,
};

export function ShowcaseContent(props: ShowcaseContentProps) {
  const getSectionUrl = (): string => {
    // note: Clicks are canceled via css in ShowcaseCarousel if not the main section.
    const { currentSection } = props;
    if (!currentSection) {
      // navigate to settings page when clicking on empty state (no section is defined)
      return '/showcase/settings/';
    }
    switch (currentSection.type) {
      case CONST.SHOWCASE_TYPE_BOARD:
        return currentSection.url;
      case CONST.SHOWCASE_TYPE_ARTICLE:
        const articleId = currentSection.id;
        return `/discover/article/${articleId}/`;
      case CONST.SHOWCASE_TYPE_SHOP:
        const { link_data } = currentSection;
        const { id: merchantID } = link_data;
        return `/shop_pins/merchants/${merchantID}/`;
      default:
        break;
    }
    return '';
  };

  const renderArticleSection = (section: ShowcaseArticleType): React.Element<any> => {
    const coverImages = section.cover_images;
    const coverUrl = coverImages[0][ARTICLE_COVER_IMAGE_SIZE].url;
    return <div className="articleImage" style={{ backgroundImage: `url(${coverUrl})` }} />;
  };

  const renderBoardSection = (section: ShowcaseBoardType) => {
    const {
      has_custom_cover: hasCustomCover,
      image_cover_hd_url: imageCoverHDUrl,
      image_cover_url: imageCoverUrl,
      images,
    } = section;

    const coverImageUrl = hasCustomCover ? imageCoverHDUrl || imageCoverUrl : '';
    const cover = coverImageUrl ? { url: coverImageUrl } : undefined;

    return (
      <WideBoardCollage
        boardThumbs={images[BOARD_THUMB_IMAGE_SIZE]}
        cover={cover}
        height={SHOWCASE_CONTENT_HEIGHT}
        numCols={SHOWCASE_BOARD_REP_COLUMNS}
        width={SHOWCASE_CONTENT_WIDTH}
      />
    );
  };

  const renderShopSection = (section: ShowcaseShopType) => (
    // $FlowFixMe: new errors found from flow 0.96 upgrade
    <ShopShowcaseSection isHovering {...section} />
  );

  const renderEmptySection = () => {
    const imageWrapperClassnames = cx(
      'emptySection',
      'bg-super-light-gray',
      'col-12',
      'flex',
      'justify-center',
      'items-center',
    );
    return (
      <div className={imageWrapperClassnames}>
        <Mask height={ADD_CIRCLE_ICON_SIZE} shape="circle" width={ADD_CIRCLE_ICON_SIZE}>
          <div className="bg-white">
            <Icon
              accessibilityLabel={i18n._('Create showcase')}
              color="red"
              icon="add-circle"
              inline
              size={ADD_CIRCLE_ICON_SIZE}
            />
          </div>
        </Mask>
      </div>
    );
  };

  const { currentSection, onEmptyStateDismissal, onClick, viewingOwnProfile } = props;

  // Choose what the main section should be.
  let renderedContent = null,
    showDismissButton = false;
  if (currentSection && currentSection.type === CONST.SHOWCASE_TYPE_BOARD) {
    renderedContent = renderBoardSection(currentSection);
  } else if (currentSection && currentSection.type === CONST.SHOWCASE_TYPE_ARTICLE) {
    renderedContent = renderArticleSection(currentSection);
  } else if (currentSection && currentSection.type === CONST.SHOWCASE_TYPE_SHOP) {
    renderedContent = renderShopSection(currentSection);
  } else if (!currentSection && viewingOwnProfile) {
    renderedContent = renderEmptySection();
    showDismissButton = true;
  } else {
    return null;
  }
  return (
    <div className="ShowcaseContent">
      <NavigateLink className="mainContentLink" onClick={onClick} to={getSectionUrl()}>
        {renderedContent}
      </NavigateLink>
      {showDismissButton && (
        <button
          className="Button borderless closeModal dismissButton"
          onClick={onEmptyStateDismissal}
          type="button"
        >
          <em />
          &nbsp;
        </button>
      )}
    </div>
  );
}

ShowcaseContent.propTypes = {
  currentSection: PropTypes.oneOfType([
    ShowcaseBoardProps,
    ShowcaseArticleProps,
    ShowcaseShopProps,
  ]),
  onEmptyStateDismissal: PropTypes.func,
  onClick: PropTypes.func,
  viewingOwnProfile: PropTypes.bool,
};

export default ShowcaseContent;
